import React from "react";
import Dialog from "@material-ui/core/Dialog";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import { makeStyles } from "@material-ui/core/styles";
import styled from "styled-components";
import NoticeEditor from "../../NoticeEditor";
import theme from "../../../../assets/theme";
import COLORS from "../../../../assets/Colors";

/**
 * Hosts the Notice Editor over the "Create Notice Template" step.
 *
 * The editor is mounted in embedded mode inside a Material-UI Dialog - the same
 * portal/dialog machinery the rest of JustAct-CM uses - so it overlays the Formik form
 * without unmounting it. The uploaded .docx is handed in as a File (already in memory
 * from the upload, so there is no S3 round-trip), the edited document comes back through
 * `onSaveDocx`, and `requestSaveConfirmation` lets the parent raise the app's standard
 * confirm dialog before anything is written.
 *
 * It is keyed by the parent on the document being edited, so each Edit click mounts a
 * fresh editor rather than reusing stale content.
 */
const useStyles = makeStyles(() => ({
  paper: {
    width: "96vw",
    maxWidth: "96vw",
    height: "92vh",
    maxHeight: "92vh",
    margin: 0,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    borderRadius: 10,
  },
}));

const NoticeTemplateEditorModal = ({
  isOpen = false,
  onClose = () => {},
  docxFile = null,
  docxUrl = "",
  templateName = "",
  onSaveDocx = async () => {},
  requestSaveConfirmation,
}) => {
  const classes = useStyles();

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      classes={{ paper: classes.paper }}
      disableRestoreFocus={true}
      disableScrollLock={true}
      /* The editor owns keyboard shortcuts (Ctrl+S, arrows, Esc inside its own panels),
         so Esc-to-close is handled by the explicit close control instead. */
      disableEscapeKeyDown={true}
      aria-labelledby="notice-template-editor-title"
    >
      <ModalHeader>
        <HeaderTitle id="notice-template-editor-title">
          Edit Notice Template
          {templateName ? <FileChip title={templateName}>{templateName}</FileChip> : null}
        </HeaderTitle>
        <IconButton size="small" onClick={onClose} aria-label="Close editor">
          <CloseIcon fontSize="small" />
        </IconButton>
      </ModalHeader>

      <EditorHost>
        
        {isOpen ? (
          <NoticeEditor
            embedded
            initialDocx={docxFile}
            initialDocxUrl={docxUrl}
            initialTemplateName={templateName}
            onSaveDocx={onSaveDocx}
            requestSaveConfirmation={requestSaveConfirmation}
            onClose={onClose}
          />
        ) : null}
      </EditorHost>
    </Dialog>
  );
};

export default NoticeTemplateEditorModal;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(0, 131, 140, 0.15);
  flex-shrink: 0;
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 16px;
  font-weight: 700;
  color: ${COLORS.COLOR_DARK};
`;

const FileChip = styled.span`
  max-width: 420px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 3px 10px;
  border-radius: 12px;
  background: rgba(0, 131, 140, 0.1);
  color: ${COLORS.BTN_GREEN};
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 12px;
  font-weight: 600;
`;

const EditorHost = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  overflow: hidden;
`;
